import { useCallback, type MutableRefObject } from "react";
import type { PlaybackClockSchedulerSample } from "./usePlaybackClock";

type TileRequestContext = {
  recordingId: string;
  speed: number;
  transportMode: string;
  timeScale: number;
  windowEnd: number;
};

type TileDecodeTelemetry = {
  startedAtMs: number;
  readyAtMs: number;
  waitMs: number;
  source: string;
  requestedLevel: number;
  actualLevel: number;
  startTimeSeconds: number;
  endTimeSeconds: number;
};

export function useEEGPlaybackSchedulerTelemetry(params: {
  currentTimeRef: MutableRefObject<number>;
  currentConfigRef: MutableRefObject<{ playback_speed?: number } | null>;
  showVideoPanel: boolean;
  recordPlaybackPerfEvent: (event: string, payload: unknown) => void;
  summarizePostCallbackActivity: (
    callbackCompletedAtMs: number,
    probeWindowMs: number,
  ) => {
    postCallbackActivityCount: number;
    postCallbackActivitySummary: string | null;
  };
  markZeroMarkerBrowserProbe: (payload: {
    callbackLateMs: number;
    playheadTimeSeconds: number | null;
    queueActivityCount: number;
    queueDelayMs: number;
    rafActivityCount: number;
    rafDelayMs: number;
  }) => void;
}) {
  const {
    currentTimeRef,
    currentConfigRef,
    showVideoPanel,
    recordPlaybackPerfEvent,
    summarizePostCallbackActivity,
    markZeroMarkerBrowserProbe,
  } = params;

  const emitPendingWindowDecodeReady = useCallback(
    ({
      queueSource,
      windowStart,
      requestContext,
      decodeTelemetry,
    }: {
      queueSource: "playing" | "paused";
      windowStart: number;
      requestContext: TileRequestContext | null;
      decodeTelemetry: TileDecodeTelemetry | null | undefined;
    }) => {
      const queuedAtMs = performance.now();
      if (!requestContext || !decodeTelemetry) {
        return queuedAtMs;
      }

      recordPlaybackPerfEvent("pendingWindowDecodeReady", {
        recordingId: requestContext.recordingId,
        queueSource,
        pendingWindowStart: Number(windowStart.toFixed(3)),
        pendingWindowEnd: Number(requestContext.windowEnd.toFixed(3)),
        playheadTime: Number(currentTimeRef.current.toFixed(3)),
        decodeStartedAtMs: decodeTelemetry.startedAtMs,
        decodeReadyAtMs: decodeTelemetry.readyAtMs,
        decodeWaitMs: decodeTelemetry.waitMs,
        readyToQueueLagMs: Number(
          (queuedAtMs - decodeTelemetry.readyAtMs).toFixed(1),
        ),
        decodeSource: decodeTelemetry.source,
        requestedLevel: decodeTelemetry.requestedLevel,
        actualLevel: decodeTelemetry.actualLevel,
        requestedStartSeconds: decodeTelemetry.startTimeSeconds,
        requestedEndSeconds: decodeTelemetry.endTimeSeconds,
        speed: requestContext.speed,
        transportMode: requestContext.transportMode,
        timeScale: requestContext.timeScale,
      });

      return queuedAtMs;
    },
    [currentTimeRef, recordPlaybackPerfEvent],
  );

  const handlePlaybackSchedulerSample = useCallback(
    (sample: PlaybackClockSchedulerSample) => {
      if (sample.scheduler !== "timer") {
        return;
      }

      if (sample.sampleKind === "companion-probe") {
        const queueProbeWindowMs = Math.max(
          sample.messageChannelDelayMs ?? 0,
          sample.zeroDelayTimerMs ?? 0,
        );
        const queueWindowActivity = summarizePostCallbackActivity(
          sample.callbackCompletedAtMs,
          queueProbeWindowMs,
        );
        const rafWindowActivity = summarizePostCallbackActivity(
          sample.callbackCompletedAtMs,
          sample.rafDelayMs ?? 0,
        );
        markZeroMarkerBrowserProbe({
          callbackLateMs: sample.callbackLateMs,
          playheadTimeSeconds: sample.playheadTimeSeconds,
          queueActivityCount: queueWindowActivity.postCallbackActivityCount,
          queueDelayMs: queueProbeWindowMs,
          rafActivityCount: rafWindowActivity.postCallbackActivityCount,
          rafDelayMs: sample.rafDelayMs ?? 0,
        });
        recordPlaybackPerfEvent("schedulerCompanionProbe", {
          scheduler: sample.scheduler,
          intervalMs: Number(sample.intervalMs.toFixed(1)),
          overrunMs: Number(sample.overrunMs.toFixed(1)),
          scheduledLateMs: Number(sample.scheduledLateMs.toFixed(1)),
          callbackLateMs: Number(sample.callbackLateMs.toFixed(1)),
          missPhase: sample.missPhase,
          rafDelayMs:
            sample.rafDelayMs === null
              ? -1
              : Number(sample.rafDelayMs.toFixed(1)),
          messageChannelDelayMs:
            sample.messageChannelDelayMs === null
              ? -1
              : Number(sample.messageChannelDelayMs.toFixed(1)),
          zeroDelayTimerMs:
            sample.zeroDelayTimerMs === null
              ? -1
              : Number(sample.zeroDelayTimerMs.toFixed(1)),
          callbackCompletedAtMs: Number(
            sample.callbackCompletedAtMs.toFixed(1),
          ),
          postCallbackActivityCount:
            queueWindowActivity.postCallbackActivityCount,
          postCallbackActivitySummary:
            queueWindowActivity.postCallbackActivitySummary,
          postCallbackQueueActivityCount:
            queueWindowActivity.postCallbackActivityCount,
          postCallbackQueueActivitySummary:
            queueWindowActivity.postCallbackActivitySummary,
          postCallbackRafActivityCount:
            rafWindowActivity.postCallbackActivityCount,
          postCallbackRafActivitySummary:
            rafWindowActivity.postCallbackActivitySummary,
          playheadTime:
            sample.playheadTimeSeconds === null
              ? Number(currentTimeRef.current.toFixed(2))
              : Number(sample.playheadTimeSeconds.toFixed(2)),
          speed: currentConfigRef.current?.playback_speed ?? null,
          surface: showVideoPanel ? "eeg-dual-video" : "eeg-only",
        });
        return;
      }

      if (sample.overrunMs < 8) {
        return;
      }

      recordPlaybackPerfEvent("schedulerStall", {
        scheduler: sample.scheduler,
        intervalMs: Number(sample.intervalMs.toFixed(1)),
        overrunMs: Number(sample.overrunMs.toFixed(1)),
        scheduledLateMs: Number(sample.scheduledLateMs.toFixed(1)),
        callbackLateMs: Number(sample.callbackLateMs.toFixed(1)),
        missPhase: sample.missPhase,
        firedDelayMs: Number(
          (sample.firedAtMs - sample.scheduledAtMs).toFixed(1),
        ),
        scheduledAtMs: Number(sample.scheduledAtMs.toFixed(1)),
        targetAtMs: Number(sample.targetAtMs.toFixed(1)),
        firedAtMs: Number(sample.firedAtMs.toFixed(1)),
        previousTickStartedAtMs:
          sample.previousTickStartedAtMs === null
            ? -1
            : Number(sample.previousTickStartedAtMs.toFixed(1)),
        previousTickCompletedAtMs:
          sample.previousTickCompletedAtMs === null
            ? -1
            : Number(sample.previousTickCompletedAtMs.toFixed(1)),
        previousTickWorkMs:
          sample.previousTickWorkMs === null
            ? -1
            : Number(sample.previousTickWorkMs.toFixed(1)),
        playheadTime:
          sample.playheadTimeSeconds === null
            ? Number(currentTimeRef.current.toFixed(2))
            : Number(sample.playheadTimeSeconds.toFixed(2)),
        speed: currentConfigRef.current?.playback_speed ?? null,
        surface: showVideoPanel ? "eeg-dual-video" : "eeg-only",
      });
    },
    [
      currentConfigRef,
      currentTimeRef,
      markZeroMarkerBrowserProbe,
      recordPlaybackPerfEvent,
      showVideoPanel,
      summarizePostCallbackActivity,
    ],
  );

  return { emitPendingWindowDecodeReady, handlePlaybackSchedulerSample };
}
